import type { TabId } from "../App";
import type { Theme } from "../lib/theme";
import type { LiveClock } from "../lib/liveClock";
import type { AuthUser } from "../lib/auth";
import InfoButton from "./InfoButton";
import UserMenu from "./UserMenu";

interface Props {
  active: TabId;
  theme: Theme;
  onThemeToggle: () => void;
  asOf: string;
  live: LiveClock;
  user: AuthUser;
  onSignOut: () => void;
}

const PAGE_TITLES: Record<TabId, { eyebrow: string; title: string }> = {
  "executive":           { eyebrow: "Overview",            title: "Overview" },
  "portfolio-billing":   { eyebrow: "Finance & Risk",      title: "Portfolio Billing" },
  "net-income-forecast": { eyebrow: "Finance & Risk",      title: "Net Income Forecast" },
  "ar-customer-risk":    { eyebrow: "Finance & Risk",      title: "AR & Customer Risk" },
  "simulator":           { eyebrow: "Finance & Risk",      title: "Simulator" },
  "decision-pack":       { eyebrow: "Finance & Risk",      title: "Decision Pack" },
  "customer-360":        { eyebrow: "Advanced Analytics",  title: "Customer 360" },
  "industry":            { eyebrow: "Advanced Analytics",  title: "Industry" },
  "cortex":              { eyebrow: "Tools",               title: "Zen Insights" },
  "chat":                { eyebrow: "Tools",               title: "Chat" },
  "kpi-reference":       { eyebrow: "Resources",           title: "KPI Reference" },
  "architecture":        { eyebrow: "Resources",           title: "Reference Architecture" },
  "faq":                 { eyebrow: "Resources",           title: "FAQ" },
  "about":               { eyebrow: "Resources",           title: "About Oraczen" },
};

const CLUSTER_BY_TAB: Record<TabId, string> = {
  "executive": "overview",
  "portfolio-billing": "finance-risk",
  "net-income-forecast": "finance-risk",
  "ar-customer-risk": "finance-risk",
  "simulator": "finance-risk",
  "decision-pack": "finance-risk",
  "customer-360": "advanced-analytics",
  "industry": "advanced-analytics",
  "cortex": "tools",
  "chat": "tools",
  "kpi-reference": "resources",
  "architecture": "resources",
  "faq": "resources",
  "about": "resources",
};

function fmtAgo(s: number): string {
  if (s < 5) return "just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s ago`;
}

export default function Topbar({ active, theme, onThemeToggle, asOf, live, user, onSignOut }: Props) {
  const nextRefreshIn = Math.max(0, live.autoRefreshSec - live.secondsAgo);
  const meta = PAGE_TITLES[active];
  const cluster = CLUSTER_BY_TAB[active];

  return (
    <header className="topbar" data-cluster={cluster}>
      <div className="topbar-page">
        <div className="topbar-eyebrow">{meta.eyebrow}</div>
        <h1 className="topbar-title">{meta.title}</h1>
      </div>

      <div className="user-cluster">
        <div className="refresh live" title={`as-of ${asOf}`}>
          <div className="refresh-label">
            Live
            <InfoButton
              info={{
                title: "Live data behavior",
                text:
                  "The dashboard polls an incremental D365 → Synapse Link → analytics endpoint on a configurable cadence (typically 5–15 min for executive views, real-time for AR aging). Auto-refresh ticks every 30s and re-derives display values to keep the surface live between full pulls.",
                foot: "Source: D365 F&O · Synapse Link · vintage v2026.18",
              }}
            />
          </div>
          <div className="refresh-time">
            <span className={`live-pulse${live.isRefreshing ? " refreshing" : ""}`} />
            refreshed {fmtAgo(live.secondsAgo)} · auto in {nextRefreshIn}s
          </div>
        </div>
        <button
          className="refresh-btn"
          onClick={live.refresh}
          aria-label="Refresh now"
          title="Refresh now"
          type="button"
          disabled={live.isRefreshing}
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" className={live.isRefreshing ? "spin" : ""}>
            <path
              d="M13.65 2.35A7 7 0 1 0 15 8h-2a5 5 0 1 1-1.46-3.54L9 7h6V1l-1.35 1.35z"
              fill="currentColor"
            />
          </svg>
        </button>
        <button className="theme-toggle" onClick={onThemeToggle} aria-label="Toggle theme" type="button">
          {theme === "light" ? (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M13.5 9.5A6 6 0 1 1 6.5 2.5a4.8 4.8 0 0 0 7 7z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="3" stroke="currentColor" strokeWidth="1.5" />
              <path
                d="M8 1.5v1.5M8 13v1.5M14.5 8H13M3 8H1.5M12.6 3.4l-1.06 1.06M4.46 11.54L3.4 12.6M12.6 12.6l-1.06-1.06M4.46 4.46L3.4 3.4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          )}
        </button>
        <UserMenu user={user} onSignOut={onSignOut} />
      </div>
    </header>
  );
}
